import React, { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  Button,
  Box,
  styled,
  Theme,
  IconButton,
  Tooltip,
} from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SensorsIcon from '@mui/icons-material/Sensors';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { PersonnelPermissions } from './modals/QuickSaleScreenSettingsModal'; // Assuming type is exported
import { useTranslation } from 'react-i18next';

// localStorage anahtarı
const ACTION_PANEL_COLLAPSED_KEY = 'gts_action_panel_collapsed';

// Styled Components (Moved from QuickSaleScreen.tsx)
const NumpadButton = styled(Button)(({ theme }) => ({
  minHeight: 45,
  height: '100%',
  fontSize: '1rem',
  borderColor: theme.palette.divider,
  padding: theme.spacing(0.5),
  '@media (max-width: 1400px)': {
    minHeight: 40,
    fontSize: '0.9rem',
  },
  '@media (max-width: 1200px)': {
    minHeight: 36,
    fontSize: '0.85rem',
  },
}));

const FunctionButton = styled(Button)(({ theme }) => ({
  minHeight: 45,
  height: '100%',
  fontSize: '0.75rem',
  padding: theme.spacing(0.5, 0.75),
  lineHeight: 1.2,
  textAlign: 'center',
  minWidth: 'auto',
  '@media (max-width: 1400px)': {
    minHeight: 40,
    fontSize: '0.7rem',
    padding: theme.spacing(0.4, 0.6),
  },
  '@media (max-width: 1200px)': {
    minHeight: 36,
    fontSize: '0.65rem',
    padding: theme.spacing(0.3, 0.5),
  },
}));

// Data structure for function buttons passed as props or defined within
export interface ActionButtonData {
  id: string;
  label: string;
  icon?: React.ReactElement;
  onClick: () => void;
  disabledCondition: boolean;
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  fullWidth?: boolean; // Optional, defaults to true for most buttons here
  variant?: 'text' | 'outlined' | 'contained'; // Optional
}

export interface ActionControlsPanelProps {
  // Props for function buttons - can be a pre-structured array or individual handlers
  // For simplicity, passing handlers that QuickSaleScreen already has
  onOpenDiscountModal: () => void;
  currentUserPermissions: PersonnelPermissions | null;
  loadingUserPermissions: boolean;
  onHoldSale: () => void;
  cartLength: number;
  onOpenPendingSalesModal: () => void;
  pendingSalesLength: number;
  onDeleteSelected: () => void;
  selectedCartItemId: string | null;
  onDecreaseSelected: () => void;
  onIncreaseSelected: () => void;
  onPartialPayment: () => void; // New prop for Parçalı Ödeme
  prepaidSystemEnabled?: boolean; // Ön ödemeli sistem modu etkin mi?
  onPosRefund?: () => void; // ✅ YENİ: POS İade modalını açma fonksiyonu
  hasPendingPosRefunds?: boolean; // ✅ YENİ: Bekleyen POS iadesi var mı?
  onOpenCustomerSelectionModal?: () => void;
  onOpenPrepaidCardSearch?: () => void;
  onOpenPurchaseItems?: () => void;
  allowAccountCreationOnSale?: boolean; // YENİ: Cari giriş işlemlerine izin ver ayarı
  onOpenAccountCreationModal?: () => void; // YENİ: Cari giriş modalını açma fonksiyonu
  isRestaurantMode?: boolean; // ✅ YENİ: Restaurant modunda mı?
  /**
   * Paket sipariş modunda mı? Restoran moduna ek olarak ürün açıklaması
   * (ItemNote) butonunun bu modda da görünmesi için kullanılır. Ürün notu
   * `cart.note` alanında tutulur ve sipariş onaylandığında
   * `PackageOrderItem.Notes` üzerinden mutfak fişine basılır.
   */
  isPackageMode?: boolean;
  waiterPermissions?: {
    canCloseSale: boolean;
    canCloseTable: boolean;
    canAddProduct: boolean;
    canRemoveProduct: boolean;
    canClearCart: boolean;
    canPrintToKitchen: boolean;
    canDeletePrintedItems: boolean;
    canDeleteUnprintedItems?: boolean; // ✅ YENİ: Yazdırılmamış ürün silme yetkisi
    canTransferTable: boolean; // ✅ YENİ: Masa taşıma yetkisi
  }; // ✅ YENİ: Garson yetkileri (restaurant modu için)
  onTransferTable?: () => void;
  onTransferItems?: () => void;
  onSplitCart?: () => void;
  onClearCart?: () => void; // ✅ YENİ: Sepeti temizle fonksiyonu
  canClearCart?: boolean; // ✅ YENİ: Sepet temizleme yetkisi var mı? (restaurant modu için)
  canPosRefund?: boolean; // ✅ YENİ: POS İade yetkisi var mı?
  restaurantLoginType?: 'waiter' | 'cashier'; // Restoran modunda garson mu kasiyer mi giriş yapmış
  onAddItemNote?: () => void; // Ürün açıklaması/notu ekleme

  // Kulüp Girişi (Club Entry) modu
  isClubEntryMode?: boolean;
  onOpenActiveEntries?: () => void;
  onOpenDirectTrigger?: () => void;
  onOpenCardReturn?: () => void;
  clubEntryDirectTriggerEnabled?: boolean;
  /** ✅ YENİ: Rezervasyon sistemi (Phase 1: TicketReservations listele) açık mı? */
  clubEntryReservationEnabled?: boolean;
  /** ✅ YENİ: Rezervasyonlar modalını aç */
  onOpenClubReservations?: () => void;
  /** ✅ YENİ: Kulüp girişinde ödeme anında kart okutma modalı açık mı?
   *  Kapalı (false) ise kulüp girişi modunda parçalı ödeme butonu gösterilir. */
  clubEntryCardScanModalEnabled?: boolean;

  // Props for Numpad
  onNumpadClick: (value: string) => void;
  
  // YENİ: Daraltılmış durumu parent'a bildirmek için callback
  onCollapsedChange?: (isCollapsed: boolean) => void;
  // isQuantityModeActive: boolean; // Numpad behavior might depend on this, but not directly for rendering numpad keys
  // quantityInputValue: string; // Similarly, for display on a quantity button, not numpad keys themselves
}

const ActionControlsPanel: React.FC<ActionControlsPanelProps> = ({
  onOpenDiscountModal,
  currentUserPermissions,
  loadingUserPermissions,
  onHoldSale,
  cartLength,
  onOpenPendingSalesModal,
  pendingSalesLength,
  onDeleteSelected,
  selectedCartItemId,
  onDecreaseSelected,
  onIncreaseSelected,
  onPartialPayment,
  onNumpadClick,
  prepaidSystemEnabled = false, // Varsayılan değer: false
  onPosRefund, // ✅ YENİ
  hasPendingPosRefunds = false, // ✅ YENİ: Varsayılan false
  onOpenCustomerSelectionModal,
  onOpenPrepaidCardSearch,
  onOpenPurchaseItems,
  allowAccountCreationOnSale = false,
  onOpenAccountCreationModal,
  onCollapsedChange, // YENİ: Daraltma durumu callback
  isRestaurantMode = false, // ✅ YENİ: Restaurant modu
  isPackageMode = false,
  waiterPermissions, // ✅ YENİ: Garson yetkileri
  onTransferTable,
  onTransferItems,
  onSplitCart,
  onClearCart, // ✅ YENİ: Sepeti temizle fonksiyonu
  canClearCart = true,
  canPosRefund = true,
  restaurantLoginType,
  isClubEntryMode = false,
  onOpenActiveEntries,
  onOpenDirectTrigger,
  onOpenCardReturn,
  clubEntryDirectTriggerEnabled = false,
  clubEntryReservationEnabled = false,
  onOpenClubReservations,
  clubEntryCardScanModalEnabled = true,
  onAddItemNote,
}) => {
  const { t } = useTranslation('salesSystem');

  // Daraltma durumunu localStorage'dan yükle
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(ACTION_PANEL_COLLAPSED_KEY);
    return saved === 'true';
  });

  // localStorage'a kaydet ve parent'ı bilgilendir
  useEffect(() => {
    localStorage.setItem(ACTION_PANEL_COLLAPSED_KEY, String(isCollapsed));
    // Parent'a bildir
    if (onCollapsedChange) {
      onCollapsedChange(isCollapsed);
    }
  }, [isCollapsed, onCollapsedChange]);

  // Toggle fonksiyonu
  const toggleCollapsed = () => {
    setIsCollapsed(prev => !prev);
  };

  // ✅ YENİ: Efektif silme izni — canRemoveProduct VEYA canDeleteUnprintedItems.
  // Yazdırılmamış ürün silme yetkisi olan garson Sil/Azalt butonlarını kullanabilir;
  // yazdırılmış satır seçiliyse guard'lar (QuickSaleScreen + hook) engeller.
  const canRemoveEffective = !!(waiterPermissions?.canRemoveProduct || waiterPermissions?.canDeleteUnprintedItems);

  // Reconstruct button data logic from QuickSaleScreen
  const dynamicFunctionButtons: ActionButtonData[] = [
    { 
      id: 'sil', 
      label: t('actionControls.buttons.delete'), 
      icon: <DeleteOutlineIcon />, 
      onClick: () => {
        console.log('🟥 [ACTION CONTROLS] SİL butonu tıklandı', { 
          selectedCartItemId, 
          isRestaurantMode,
          canRemoveProduct: waiterPermissions?.canRemoveProduct,
          canDeleteUnprintedItems: waiterPermissions?.canDeleteUnprintedItems,
          canDeletePrintedItems: waiterPermissions?.canDeletePrintedItems,
        });
        onDeleteSelected();
      }, 
      disabledCondition: selectedCartItemId === null || (isRestaurantMode && !canRemoveEffective), 
      color: 'error' as const 
    },
    { 
      id: 'azalt', 
      label: t('actionControls.buttons.decrease'), 
      icon: <RemoveIcon />, 
      onClick: () => {
        console.log('🟦 [ACTION CONTROLS] AZALT butonu tıklandı', { selectedCartItemId });
        onDecreaseSelected();
      }, 
      disabledCondition: selectedCartItemId === null || (isRestaurantMode && !canRemoveEffective) 
    },
    { 
      id: 'arttir', 
      label: t('actionControls.buttons.increase'), 
      icon: <AddIcon />, 
      onClick: () => {
        console.log('🟦 [ACTION CONTROLS] ARTTIR butonu tıklandı', { selectedCartItemId });
        onIncreaseSelected();
      }, 
      disabledCondition: selectedCartItemId === null || (isRestaurantMode && !waiterPermissions?.canAddProduct) 
    },
  ];

  if ((isRestaurantMode || isPackageMode) && onAddItemNote) {
    dynamicFunctionButtons.push({ 
      id: 'aciklama', 
      label: t('actionControls.buttons.note'), 
      icon: <NoteAddIcon />, 
      onClick: onAddItemNote, 
      disabledCondition: selectedCartItemId === null, 
      color: 'info' as const 
    });
  }

  if (isRestaurantMode && onTransferTable) {
    dynamicFunctionButtons.push({ 
      id: 'masaAktar', 
      label: t('actionControls.buttons.transferTable'), 
      icon: <SwapHorizIcon />, 
      onClick: onTransferTable, 
      disabledCondition: cartLength === 0 || !waiterPermissions?.canTransferTable, 
      color: 'secondary' as const 
    });
  }

  if (isRestaurantMode && onTransferItems) {
    dynamicFunctionButtons.push({ 
      id: 'urunAktar', 
      label: t('actionControls.buttons.transferItem'), 
      icon: <DriveFileMoveIcon />, 
      onClick: onTransferItems, 
      disabledCondition: cartLength === 0 || !waiterPermissions?.canTransferTable, 
      color: 'secondary' as const 
    });
  }

  // ✅ YENİ: POS İade butonu - HER ZAMAN EKLE (pasif veya aktif)
  if (onPosRefund) {
    dynamicFunctionButtons.push({ 
      id: 'posIade', 
      label: t('actionControls.buttons.posRefund'), 
      icon: <CreditCardOffIcon />, 
      onClick: onPosRefund, 
      disabledCondition: !hasPendingPosRefunds || canPosRefund === false, // ✅ Bekleyen iade yoksa VEYA yetki yoksa pasif
      color: undefined // ✅ Diğer butonlar gibi renklendirme
    });
  }

  // ✅ YENİ: Sepet Böl butonu
  if (onSplitCart) {
    dynamicFunctionButtons.push({ 
      id: 'sepetBol', 
      label: t('actionControls.buttons.splitCart'), 
      icon: <CallSplitIcon />, 
      onClick: onSplitCart, 
      disabledCondition: cartLength === 0, // Sepet boşsa pasif
      color: 'info' as const
    });
  }

  // ✅ YENİ: Sepeti Temizle butonu - EN SONA EKLE
  if (onClearCart) {
    dynamicFunctionButtons.push({ 
      id: 'sepetiTemizle', 
      label: t('actionControls.buttons.clearCart'), 
      icon: <DeleteSweepIcon />, 
      onClick: onClearCart, 
      disabledCondition: cartLength === 0 || canClearCart === false, // Sepet boşsa veya yetkisizse pasif
      color: 'error' as const
    });
  }

  const currentFunctionButtons: ActionButtonData[] = [];

  if (!isClubEntryMode) {
    currentFunctionButtons.push(
      {
        id: 'parcali',
        label: t('actionControls.buttons.partialPayment'),
        icon: <PaymentsIcon />,
        onClick: onPartialPayment,
        disabledCondition: cartLength === 0 || prepaidSystemEnabled,
        color: undefined
      },
      {
        id: 'indirim',
        label: t('actionControls.buttons.discount'),
        icon: <PercentIcon />,
        onClick: onOpenDiscountModal,
        // Ön ödemeli modda da aktif — indirim tüm modlarda yetkiye bağlıdır.
        disabledCondition: loadingUserPermissions || !currentUserPermissions?.applyDiscount,
        color: undefined
      },
      { id: 'bekle', label: t('actionControls.buttons.hold'), icon: <PauseCircleOutlineIcon />, onClick: onHoldSale, disabledCondition: cartLength === 0 || prepaidSystemEnabled, color: undefined },
      { id: 'bekleyenler', label: t('actionControls.buttons.pending'), icon: <PlaylistPlayIcon />, onClick: onOpenPendingSalesModal, disabledCondition: pendingSalesLength === 0 || prepaidSystemEnabled, color: undefined },
    );
  }

  if (isClubEntryMode) {
    // ✅ YENİ: Ödeme anında kart okutma modalı KAPALI ise (cardScanModalEnabled === false)
    //    işlem normal hızlı satış gibi tamamlandığından parçalı ödeme butonu gösterilir.
    if (!clubEntryCardScanModalEnabled) {
      currentFunctionButtons.push({
        id: 'parcali',
        label: t('actionControls.buttons.partialPayment'),
        icon: <PaymentsIcon />,
        onClick: onPartialPayment,
        disabledCondition: cartLength === 0 || prepaidSystemEnabled,
        color: undefined
      });
    }
    if (onOpenActiveEntries) {
      currentFunctionButtons.push({
        id: 'clubActiveEntries',
        label: t('actionControls.activeEntries'),
        icon: <PeopleAltIcon />,
        onClick: onOpenActiveEntries,
        disabledCondition: false,
        color: 'info' as const
      });
    }
    // ✅ İndirim butonu kulüp girişi modunda da gösterilir (normal moddaki ile aynı
    //    yetki kuralı). Kasiyer sistemi aktif + kasiyerde "İndirim Uygula" yetkisi varsa
    //    aktif olur; yetki yoksa pasif görünür.
    currentFunctionButtons.push({
      id: 'indirim',
      label: t('actionControls.buttons.discount'),
      icon: <PercentIcon />,
      onClick: onOpenDiscountModal,
      // Ön ödemeli modda da aktif — indirim tüm modlarda yetkiye bağlıdır.
      disabledCondition: loadingUserPermissions || !currentUserPermissions?.applyDiscount,
      color: undefined
    });
    if (onOpenDirectTrigger && clubEntryDirectTriggerEnabled) {
      currentFunctionButtons.push({
        id: 'clubDirectTrigger',
        label: t('actionControls.buttons.trigger'),
        icon: <SensorsIcon />,
        onClick: onOpenDirectTrigger,
        disabledCondition: false,
        color: 'warning' as const
      });
    }
    if (onOpenCardReturn) {
      currentFunctionButtons.push({
        id: 'clubCardReturn',
        label: t('actionControls.cardReturn'),
        icon: <AssignmentReturnIcon />,
        onClick: onOpenCardReturn,
        disabledCondition: false,
        color: 'secondary' as const
      });
    }
    // ✅ YENİ: Rezervasyonlar butonu (sadece ayar açıkken)
    if (clubEntryReservationEnabled && onOpenClubReservations) {
      currentFunctionButtons.push({
        id: 'clubReservations',
        label: t('actionControls.reservations'),
        icon: <EventNoteIcon />,
        onClick: onOpenClubReservations,
        disabledCondition: false,
        color: 'primary' as const
      });
    }
  }

  // ✅ GÜNCELLEME: Buton sıralaması - En sona Sil, ondan sonra Sepeti Temizle
  const allFunctionButtons = [
    ...currentFunctionButtons,
    ...dynamicFunctionButtons.filter(btn => !['sil', 'posIade', 'sepetiTemizle'].includes(btn.id)), // Sil, POS İade ve Sepeti Temizle'yi filtrele
    ...dynamicFunctionButtons.filter(btn => btn.id === 'posIade'), // POS İade'yi ekle
  ];
  
  // Sil butonunu ekle
  const silBtn = dynamicFunctionButtons.find(btn => btn.id === 'sil');
  if (silBtn) {
    allFunctionButtons.push(silBtn);
  }
  
  // ✅ YENİ: Sepeti Temizle butonunu en sona ekle
  const sepetiTemizleBtn = dynamicFunctionButtons.find(btn => btn.id === 'sepetiTemizle');
  if (sepetiTemizleBtn) {
    allFunctionButtons.push(sepetiTemizleBtn);
  }

  const renderFunctionButtons = () => {
    const buttonElements: React.ReactElement[] = [];
    if (allFunctionButtons && allFunctionButtons.length > 0) {
      // Ön ödemeli sistemde belirli butonları filtrele
      // NOT: 'indirim' bilinçli olarak filtrede DEĞİL — indirim tüm modlarda
      // (ön ödemeli dahil) kullanılabilir; görünürlüğü yetki koşulu belirler.
      let buttonsToRender = prepaidSystemEnabled 
        ? allFunctionButtons.filter(btn => !['parcali', 'bekle', 'bekleyenler', 'sepetBol', 'posIade'].includes(btn.id))
        : allFunctionButtons;
      
      // ✅ YENİ: Restaurant modunda belirli butonları gizle ve yetki kontrolü
      if (isRestaurantMode) {
        buttonsToRender = buttonsToRender.filter(btn => {
          // Her zaman gizlenecek butonlar
          if (['bekle', 'bekleyenler'].includes(btn.id)) {
            return false;
          }

          // İndirim butonu: sadece kasiyer giriş yapmışsa ve CanApplyDiscount yetkisi varsa
          if (btn.id === 'indirim') {
            return restaurantLoginType === 'cashier' && !!currentUserPermissions?.applyDiscount;
          }
          
          // Yetki kontrolü ile gizlenecek butonlar
          if (waiterPermissions) {
            // Parçalı Ödeme → Hesap Kapatma yetkisi gerekir
            if (btn.id === 'parcali' && !waiterPermissions.canCloseSale) {
              return false;
            }
            // ✅ Sil butonu → Ürün Silme VEYA Yazdırılmamış Ürün Silme yetkisi gerekir
            if (btn.id === 'sil' && !canRemoveEffective) {
              return false;
            }
          }
          
          return true;
        });
      }
      
      if (prepaidSystemEnabled) {
        buttonElements.push(
          <Grid item xs={12} key="satinAlinanlar" sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            {isCollapsed ? (
              <Tooltip title={t('actionControls.buttons.purchasedItems')} placement="left">
                <IconButton 
                  onClick={onOpenPurchaseItems || (() => {})} 
                  color="info"
                  sx={{ width: 44, height: 44 }}
                >
                  <ReceiptLongIcon />
                </IconButton>
              </Tooltip>
            ) : (
            <FunctionButton 
              fullWidth 
              variant="contained" 
              onClick={onOpenPurchaseItems || (() => {})} 
              color="info" 
              sx={{ height: 60, fontWeight: 'bold' }}
              startIcon={<ReceiptLongIcon />}
            >
              {t('actionControls.buttons.purchasedItems')}
            </FunctionButton>
            )}
          </Grid>
        );
      }
      
      // YENİ: Cari Giriş İşlemleri ayarı etkinse Müşteri Geçiş butonunu ekle (Ön ödemeli sistem modunda değilken)
      if (allowAccountCreationOnSale && !prepaidSystemEnabled && onOpenAccountCreationModal) {
        buttonElements.push(
          <Grid item xs={12} key="musteriGecis" sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            {isCollapsed ? (
              <Tooltip title={t('actionControls.buttons.customerSwitch')} placement="left">
                <IconButton 
                  onClick={onOpenAccountCreationModal} 
                  color="info"
                  sx={{ width: 44, height: 44 }}
                >
                  <PersonAddIcon />
                </IconButton>
              </Tooltip>
            ) : (
            <FunctionButton 
              fullWidth 
              variant="contained" 
              onClick={onOpenAccountCreationModal} 
              color="info" 
              sx={{ height: 60, fontWeight: 'bold' }}
              startIcon={<PersonAddIcon />}
            >
              {t('actionControls.buttons.customerSwitch')}
            </FunctionButton>
            )}
          </Grid>
        );
      }
        
      for (let i = 0; i < buttonsToRender.length; i++) {
        const btn = buttonsToRender[i];
        if (!btn) continue;

        // Daraltılmış modda: Azalt ve Arttır butonları alt alta
        if (btn.id === 'azalt' && i + 1 < buttonsToRender.length && buttonsToRender[i + 1]?.id === 'arttir') {
          const arttirBtn = buttonsToRender[i + 1];
          if (!arttirBtn) {
            buttonElements.push(
              <Grid item xs={12} key={btn.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                {isCollapsed ? (
                  <Tooltip title={btn.label} placement="left">
                    <span>
                      <IconButton onClick={btn.onClick} disabled={btn.disabledCondition} color={btn.color || 'default'} sx={{ width: 44, height: 44 }}>
                        {btn.icon}
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                <FunctionButton 
                  fullWidth 
                  variant={btn.variant || "outlined"} 
                  startIcon={btn.icon} 
                  onClick={btn.onClick} 
                  disabled={btn.disabledCondition} 
                  color={btn.color || 'inherit'}
                >
                  {btn.label}
                </FunctionButton>
                )}
              </Grid>
            );
            continue;
          }
          
          if (isCollapsed) {
            // Daraltılmış mod: Alt alta
            buttonElements.push(
              <Grid item xs={12} key={btn.id} sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                <Tooltip title={btn.label} placement="left">
                  <span>
                    <IconButton onClick={btn.onClick} disabled={btn.disabledCondition} color={btn.color || 'default'} sx={{ width: 44, height: 44 }}>
                      {btn.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            );
            buttonElements.push(
              <Grid item xs={12} key={arttirBtn.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip title={arttirBtn.label} placement="left">
                  <span>
                    <IconButton onClick={arttirBtn.onClick} disabled={arttirBtn.disabledCondition} color={arttirBtn.color || 'default'} sx={{ width: 44, height: 44 }}>
                      {arttirBtn.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            );
          } else {
            // Genişletilmiş mod: Yan yana
          buttonElements.push(
              <Grid item xs={12} key={`${btn.id}-${arttirBtn.id}-row`} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <FunctionButton 
                    fullWidth 
                    variant={btn.variant || "outlined"} 
                    startIcon={btn.icon} 
                    onClick={btn.onClick} 
                    disabled={btn.disabledCondition} 
                    color={btn.color || 'inherit'}
                  >
                    {btn.label}
                  </FunctionButton>
                </Grid>
                <Grid item xs={6}>
                  <FunctionButton 
                    fullWidth 
                    variant={arttirBtn.variant || "outlined"} 
                    startIcon={arttirBtn.icon} 
                    onClick={arttirBtn.onClick} 
                    disabled={arttirBtn.disabledCondition} 
                    color={arttirBtn.color || 'inherit'}
                  >
                    {arttirBtn.label}
                  </FunctionButton>
                </Grid>
              </Grid>
            </Grid>
          );
          }
          i++;
        } else {
          buttonElements.push(
            <Grid item xs={12} key={btn.id} sx={{ display: 'flex', justifyContent: 'center' }}>
              {isCollapsed ? (
                <Tooltip title={btn.label} placement="left">
                  <span>
                    <IconButton onClick={btn.onClick} disabled={btn.disabledCondition} color={btn.color || 'default'} sx={{ width: 44, height: 44 }}>
                      {btn.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
              <FunctionButton 
                fullWidth 
                variant={btn.variant || "outlined"} 
                startIcon={btn.icon} 
                onClick={btn.onClick} 
                disabled={btn.disabledCondition} 
                color={btn.color || 'inherit'}
              >
                {btn.label}
              </FunctionButton>
              )}
            </Grid>
          );
        }
      }
    }
    return buttonElements;
  };

  const numpadKeys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '0', '.', 'C', '<', 'Enter'];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflowY: 'auto',
        pr: 0,
        mr: 0,
        transition: 'all 0.3s ease',
      }}
    >
      {/* Toggle Butonu - En Üstte - Sadece onCollapsedChange prop'u varsa göster */}
      {onCollapsedChange && (
        <Paper sx={{ p: 0, mb: 1 }}>
          <Grid container spacing={0}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              {isCollapsed ? (
                <Tooltip title={t('actionControls.expand')} placement="left">
                  <IconButton 
                    onClick={toggleCollapsed}
                    color="primary"
                    size="small"
                    sx={{ 
                      width: 40, 
                      height: 40,
                      border: '2px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <FunctionButton 
                  fullWidth 
                  variant="outlined" 
                  onClick={toggleCollapsed}
                  startIcon={<ChevronRightIcon fontSize="small" />}
                  color="primary"
                  sx={{ height: 40, fontWeight: 'bold', fontSize: '0.7rem' }}
                >
                  {t('actionControls.collapse')}
                </FunctionButton>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Fonksiyon Butonları */}
      <Paper sx={{ p: 0, mb: 1 }}>
        <Grid container spacing={0.5}>
          {renderFunctionButtons()}
        </Grid>
      </Paper>

      {prepaidSystemEnabled && (
        <Paper sx={{ p: 0, my: 1 }}>
          <Grid container spacing={0}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              {isCollapsed ? (
                <Tooltip title={t('actionControls.buttons.changeCard')} placement="left">
                  <IconButton 
                    onClick={onOpenPrepaidCardSearch || (() => {})} 
                    color="primary"
                    sx={{ width: 44, height: 44 }}
                  >
                    <PersonAddIcon />
                  </IconButton>
                </Tooltip>
              ) : (
              <FunctionButton 
                fullWidth 
                variant="contained" 
                onClick={onOpenPrepaidCardSearch || (() => {})} 
                color="primary"
              >
                {t('actionControls.buttons.changeCard')}
              </FunctionButton>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Numpad - Sadece genişletilmiş modda ve restaurant modu DEĞİLSE göster */}
      {!isCollapsed && !isRestaurantMode && (
        <Paper sx={{ p: 1, mt: 1, display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={0.5} sx={{ height: 'auto' }}>
          {numpadKeys.map((key) => {
            // Grid kolon boyutlarını belirle
            let gridSize = 4; // Varsayılan: 7,8,9 / 4,5,6 / 1,2,3 için
            
            if (key === '0' || key === 'Enter') {
              gridSize = 12; // 0 ve Enter için tam genişlik
            } else if (key === '.' || key === 'C' || key === '<') {
              gridSize = 4; // ., C, < için 4'er kolon (toplam 12)
            }
            
            return (
              <Grid 
                item 
                xs={gridSize} 
                key={key} 
                sx={{ 
                  minHeight: {
                    xs: '45px',
                    sm: '48px',
                    md: '50px',
                    lg: '52px',
                    xl: '55px'
                  },
                  display: 'flex'
                }}
              >
                <NumpadButton
                  fullWidth
                  variant={key === 'Enter' ? "contained" : key === '.' ? "contained" : "outlined"}
                  onClick={() => onNumpadClick(key)}
                  color={key === 'C' || key === '<' ? 'error' : key === 'Enter' ? 'primary' : key === '.' ? 'info' : 'inherit'}
                  sx={{
                    fontWeight: (key === 'Enter' || key === 'C' || key === '<' || key === '.') ? 'bold' : 'normal',
                  }}
                >
                  {key}
                </NumpadButton>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
      )}
      <Box sx={{ flexGrow: 1 }} />
    </Box>
  );
};

export default ActionControlsPanel; 